import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Table, Form, Input, Pagination, Typography, Button, Empty } from "antd";
import type { FormInstance } from "antd";
import type { PaginationProps } from "antd/lib";
import { EvaluationStudent } from "../../../../../types/course-management";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { itemRenderPaging } from "../../../../../utils/functionUltils";

const { Text } = Typography;

interface EditableTableProps {
	form: FormInstance;
	data: EvaluationStudent[];
	isEditingAll: boolean;
	onSaveRow: (updatedStudent: EvaluationStudent) => void;
	pageNumber: number;
	pageSize: number;
	totalPage: number;
	isLoading: boolean;
	setPageNumber: (page: number) => void;
	setPageSize: (size: number) => void;
	isEditDisabled: boolean
}


const EditableTable: React.FC<EditableTableProps> = (props) => {
  const { t } = useTranslation();
	const {
		form,
		data,
		isEditingAll,
		onSaveRow,
		pageNumber,
		pageSize,
		totalPage,
		isLoading,
		setPageNumber,
		setPageSize,
		isEditDisabled
	} = props;

	const [editingKey, setEditingKey] = useState<number | null>(null);

	useEffect(() => {
		const fieldsValue: Record<string, any> = {};
		data.forEach((item) => {
			fieldsValue[`score_${item.id}`] = item.score || 0;
		});
		form.setFieldsValue(fieldsValue);
	}, [data, form]);

	const handleEditRow = (record: EvaluationStudent) => {
		setEditingKey(record.id);
		form.setFieldsValue({ [`score_${record.id}`]: record.score || 0 });
	};

	const handleSaveRowSingle = async (record: EvaluationStudent) => {
		try {
			await form.validateFields([`score_${record.id}`]);
			const val = form.getFieldValue(`score_${record.id}`);
			onSaveRow({ ...record, score: val != null ? Number(val) : 0 });
			setEditingKey(null);
		} catch (error) {
			console.error("Validate error:", error);
		}
	};

	const handleSaveRowInEditAll = async (record: EvaluationStudent) => {
		try {
			await form.validateFields([`score_${record.id}`]);
			const val = form.getFieldValue(`score_${record.id}`);
			onSaveRow({ ...record, score: val != null ? Number(val) : 0 });
		} catch (error) {
			console.error("Validate error:", error);
		}
	};

	const columns: ColumnsType<EvaluationStudent> = [
		{
			title: "STT",
			render: (_value, _record, index) =>
				pageSize * (pageNumber - 1) + (index + 1),
			width: 70,
		},
		{
			title: t("Học viên", "Học viên"),
			dataIndex: "studentName",
			key: "studentName",
		},
		{
			title: "Email",
			dataIndex: "studentEmail",
			key: "studentEmail",
		},
		{
			title: t("Điểm", "Điểm"),
			dataIndex: "score",
			key: "score",
			fixed: 'right',
			width: 100,
			render: (value: number | null, record) => {
				if (isEditingAll) {
					return (
						<Form.Item
							name={`score_${record.id}`}
							style={{ margin: 0 }}
						>
							<Input type="number" />
						</Form.Item>
					);
				}
				if (editingKey === record.id) {
					return (
						<Form.Item
							name={`score_${record.id}`}
							style={{ margin: 0 }}
						>
							<Input type="number" />
						</Form.Item>
					);
				}
				return <Text>{value ?? "Chưa có điểm"}</Text>;
			},
		},
		{
			title: t("Hành động", "Hành động"),
			key: "action",
			width: 150,
			fixed: 'right',
			render: (_val, record) => {
				if (isEditingAll) {
					return (
						<Button
							type="link"
							icon={<SaveOutlined />}
							onClick={() => handleSaveRowInEditAll(record)}
						>
							{t("Lưu dòng", "Lưu dòng")}
						</Button>
					);
				}
				if (editingKey === record.id) {
					return (
						<Button
							type="link"
							icon={<SaveOutlined />}
							onClick={() => handleSaveRowSingle(record)}
						>
							{t("Lưu", "Lưu")}
						</Button>
					);
				} else {
					return (
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => handleEditRow(record)}
							disabled={isEditDisabled}
						>
							{t("Sửa", "Sửa")}
						</Button>
					);
				}
			},
		},
	];

	const onShowSizeChange: PaginationProps["onShowSizeChange"] = (_, size) => {
		setPageSize(size);
		setPageNumber(1);
		setEditingKey(null);
	};
	const onChange: PaginationProps['onChange'] = (pageNumber) => {
		setPageNumber(pageNumber);
	};
	return (
		<Form form={form} component={false}>
			<Table<EvaluationStudent>
				dataSource={data}
				columns={columns}
				loading={isLoading}
				pagination={false}
				rowKey="id"
				bordered
				scroll={{ x: 1500, y: 600 }}
				locale={{ emptyText: <Empty description={t("Không tìm thấy", "Không tìm thấy")} /> }}

			/>
			<Pagination
				total={totalPage}
				showSizeChanger
				pageSize={pageSize}
				current={pageNumber}
				className="pagination-table"
				onChange={onChange}
				onShowSizeChange={onShowSizeChange}
				itemRender={itemRenderPaging}
				locale={{
					items_per_page: "/ trang",
					jump_to: t("Đi đến trang", "Đi đến trang"),
					page: "",
				}}
				showQuickJumper
				showTotal={(total) => `${t("Tổng số", "Tổng số")} ${total} ${t("bản ghi", "bản ghi")}`}
			/>
		</Form>
	);
};

export default EditableTable;
